/**
 * Доля хвоста публичного рендерера: вид `feature_chips` (плашки-фичи).
 *
 * WEB-2920, заход 2: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'feature_chips'` и хуки остаются в фасаде; ветка вида —
 * функция отрисовки `renderFeatureChips(ctx)`, замыкания фасада приходят
 * объектом-контекстом (`ExtendedRenderContext`). Эту долю импортирует только
 * фасад: она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import type React from 'react';
import { hexToRgba } from '../blockBackgroundStyle';
import { resolveBlockBorder } from '../blockBorder';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

/**
 * WEB-1829 — «Плашки-фичи»: сетка карточек «иконка + короткая подпись»
 * (пример владельца 31.08: 2×3 тёмных плашки, бирюзовые иконки, белый
 * текст). Одна ветка на обе поверхности — квиз и пейвол ходят через
 * общий PublicBlockRenderer, который делегирует сюда.
 *
 * ЗАЛИВКА ПЛАШКИ — полупрозрачная ТЁМНАЯ и на светлой теме тоже: канон
 * подписи-пилюли поверх фото (WEB-1684, «тёмная плашка, белый текст»).
 * Сплошной цвет по переменной темы дал бы на светлой теме белую плашку
 * на белом фоне — невидимую сетку. Полупрозрачность вместо глухого
 * чёрного нужна, чтобы плашка ложилась и на картинку-фон экрана.
 *
 * РАМКА — только через resolveBlockBorder: зашитая в разметке строка
 * `border: '1px …'` покраснила бы счётчик сторожа WEB-1697.
 *
 * Ветка `feature_chips` фасада — тело `case` слово в слово.
 */
export function renderFeatureChips(
  ctx: ExtendedRenderContext,
): React.ReactElement | null {
  const { block, contentTextColor, interp } = ctx;
  const chips = block.featureChipItems ?? [];
  if (chips.length === 0) return null;
  // Колонок 1..3, пусто = 2 (как на примере). Мусор из базы не должен
  // разваливать сетку, поэтому значение зажимается, а не берётся на веру.
  const rawColumns = block.featureChipColumns;
  const columns =
    typeof rawColumns === 'number' && Number.isFinite(rawColumns)
      ? Math.min(3, Math.max(1, Math.round(rawColumns)))
      : 2;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 'var(--option-gap, 12px)',
        marginBottom: 16,
      }}
    >
      {chips.map((chip, i) => (
        <div
          key={chip.id ?? i}
          data-testid={`feature-chip-${i}`}
          style={{
            ...resolveBlockBorder(block),
            // Заливка ЭТОЙ плашки сильнее умолчания блока (WEB-1829).
            // Плотность (заказ владельца 01.09) — одна на блок: авторский
            // hex прогоняется через hexToRgba (альфа 8-значного hex
            // ПЕРЕМНОЖАЕТСЯ, канон WEB-955), умолчание меняет свои 0.35.
            background:
              typeof block.featureChipBackgroundOpacity === 'number'
                ? chip.background
                  ? hexToRgba(
                      chip.background,
                      block.featureChipBackgroundOpacity,
                    )
                  : `rgba(0, 0, 0, ${block.featureChipBackgroundOpacity})`
                : chip.background || 'rgba(0, 0, 0, 0.35)',
            display: 'flex',
            alignItems: 'center',
            // Живая сверка с примером владельца (01.09): при gap 10 и
            // паддинге 14 тексту двухколоночной сетки на 390px остаётся
            // ~94px, и «показателями»/«Еженедельные» рвутся посреди
            // слова. 8/10 отдают тексту ~104px — слова переносятся
            // целиком, как на примере.
            gap: 8,
            padding: '12px 10px',
            // Подпись переносится по словам, как на примере (две строки),
            // а не режется многоточием: слов тут мало и они значимые.
            minWidth: 0,
          }}
        >
          {chip.iconUrl ? (
            <img
              data-testid={`feature-chip-${i}-icon`}
              src={chip.iconUrl}
              alt=""
              // WEB-1994: коробка иконки зашита (22×22) — место под неё
              // зарезервировано и до загрузки; атрибуты повторяют её,
              // чтобы пропорция была известна браузеру без стилей.
              width={22}
              height={22}
              decoding="async"
              style={{
                width: 22,
                height: 22,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          ) : chip.emoji ? (
            <span
              data-testid={`feature-chip-${i}-emoji`}
              style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}
            >
              {chip.emoji}
            </span>
          ) : null}
          <span
            style={{
              // Старшинство объявлено: цвет ЭТОЙ плашки → общий цвет
              // блока/воронки (WEB-1702) → белый. Фолбэк белый, потому
              // что плашка тёмная на обеих темах (см. докблок ветки).
              color: chip.textColor || contentTextColor || '#ffffff',
              fontSize: chip.fontSize ?? 14,
              lineHeight: 1.3,
              minWidth: 0,
              // Живая сверка с примером владельца (01.09): 'anywhere'
              // рвал ЦЕЛЫЕ слова («показателям / и»), хотя слово влезало
              // строкой ниже. 'break-word' переносит по словам и режет
              // слово только когда оно само шире плашки.
              overflowWrap: 'break-word',
            }}
          >
            {interp(chip.text)}
          </span>
        </div>
      ))}
    </div>
  );
}
